import { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MapPin, ChevronRight, ChevronLeft, Check, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { cn, apiRequest, CITIES, SERVICES_LIST } from '../lib/utils';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, title: 'Service', icon: Calendar },
  { id: 2, title: 'Your Info', icon: User },
  { id: 3, title: 'Schedule', icon: Clock },
  { id: 4, title: 'Confirm', icon: Check },
];

const TIME_SLOTS = [
  'Morning (8:00 AM - 12:00 PM)',
  'Early Afternoon (12:00 PM - 2:00 PM)',
  'Late Afternoon (2:00 PM - 5:00 PM)',
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export default function AppointmentModal({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    service_needed: '',
    name: '',
    email: '',
    phone: '',
    city: '',
    preferred_times: [],
    notes: '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleTimeSlot = (day, time) => {
    const slot = `${day} - ${time}`;
    setFormData(prev => ({
      ...prev,
      preferred_times: prev.preferred_times.includes(slot)
        ? prev.preferred_times.filter(t => t !== slot)
        : [...prev.preferred_times, slot]
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!formData.service_needed;
      case 2: return formData.name && formData.email && formData.phone && formData.city;
      case 3: return formData.preferred_times.length > 0;
      case 4: return true;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await apiRequest('/public/appointments', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      toast.success('Appointment request submitted! We will call you to confirm.');
      onOpenChange(false);
      setStep(1);
      setFormData({
        service_needed: '',
        name: '',
        email: '',
        phone: '',
        city: '',
        preferred_times: [],
        notes: '',
      });
    } catch (error) {
      toast.error('Failed to submit. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden" data-testid="appointment-modal">
        <DialogHeader className="bg-slate-900 text-white p-6">
          <DialogTitle className="text-xl font-semibold">Schedule Your Consultation</DialogTitle>
          <p className="text-slate-400 text-sm mt-1">Choose your preferred times and we'll call to confirm</p>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b bg-slate-50">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center">
                <div className={cn(
                  "flex items-center gap-2 transition-colors",
                  step >= s.id ? "text-orange-600" : "text-slate-400"
                )}>
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                    step > s.id ? "bg-orange-600 text-white" :
                    step === s.id ? "bg-orange-100 text-orange-600 border-2 border-orange-600" :
                    "bg-slate-200 text-slate-500"
                  )}>
                    {step > s.id ? <Check className="w-4 h-4" /> : s.id}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{s.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className={cn(
                    "w-4 h-4 mx-2",
                    step > s.id ? "text-orange-600" : "text-slate-300"
                  )} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 min-h-[300px]">
          {/* Step 1: Service Selection */}
          {step === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <Label className="text-sm font-medium text-slate-700">What service do you need?</Label>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {SERVICES_LIST.map((service) => (
                    <button
                      key={service}
                      onClick={() => updateField('service_needed', service)}
                      className={cn(
                        "p-4 rounded-xl border-2 text-left transition-all hover:border-orange-300",
                        formData.service_needed === service
                          ? "border-orange-600 bg-orange-50"
                          : "border-slate-200 hover:bg-slate-50"
                      )}
                      data-testid={`service-option-${service.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <span className={cn(
                        "text-sm font-medium",
                        formData.service_needed === service ? "text-orange-600" : "text-slate-700"
                      )}>
                        {service}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Personal Info */}
          {step === 2 && (
            <div className="space-y-4 animate-fade-in">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="pl-10"
                      placeholder="John Smith"
                      data-testid="appt-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="pl-10"
                      placeholder="john@email.com"
                      data-testid="appt-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      className="pl-10"
                      placeholder="(541) 756-2121"
                      data-testid="appt-phone-input"
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="city">City</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                    <Select value={formData.city} onValueChange={(val) => updateField('city', val)}>
                      <SelectTrigger className="pl-10" data-testid="appt-city-select">
                        <SelectValue placeholder="Select your city" />
                      </SelectTrigger>
                      <SelectContent>
                        {CITIES.map((city) => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Scheduling */}
          {step === 3 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <Label className="text-sm font-medium text-slate-700">Select your preferred times (choose multiple)</Label>
                <p className="text-xs text-slate-500 mt-1">We'll call you to confirm the best available slot</p>
              </div>
              <div className="space-y-3 mt-4">
                {DAYS.map((day) => (
                  <div key={day} className="space-y-2">
                    <p className="text-sm font-medium text-slate-600">{day}</p>
                    <div className="flex flex-wrap gap-2">
                      {TIME_SLOTS.map((time) => {
                        const slot = `${day} - ${time}`;
                        const isSelected = formData.preferred_times.includes(slot);
                        return (
                          <button
                            key={slot}
                            onClick={() => toggleTimeSlot(day, time)}
                            className={cn(
                              "px-3 py-2 rounded-lg text-xs font-medium transition-all",
                              isSelected
                                ? "bg-orange-600 text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            )}
                            data-testid={`time-slot-${day.toLowerCase()}-${time.split(' ')[0].toLowerCase()}`}
                          >
                            {time.split(' ')[0]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Label htmlFor="notes">Additional Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  className="mt-1"
                  placeholder="Any specific concerns or questions?"
                  rows={3}
                  data-testid="appt-notes-input"
                />
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="font-semibold text-slate-900 mb-4">Appointment Summary</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Service:</span>
                    <span className="font-medium text-slate-900">{formData.service_needed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Name:</span>
                    <span className="font-medium text-slate-900">{formData.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Email:</span>
                    <span className="font-medium text-slate-900">{formData.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Phone:</span>
                    <span className="font-medium text-slate-900">{formData.phone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">City:</span>
                    <span className="font-medium text-slate-900">{formData.city}</span>
                  </div>
                  <div className="pt-3 border-t">
                    <span className="text-slate-500 block mb-2">Preferred Times:</span>
                    <div className="flex flex-wrap gap-2">
                      {formData.preferred_times.map((slot) => (
                        <span key={slot} className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                          {slot}
                        </span>
                      ))}
                    </div>
                  </div>
                  {formData.notes && (
                    <div className="pt-3 border-t">
                      <span className="text-slate-500 block mb-1">Notes:</span>
                      <span className="text-slate-700">{formData.notes}</span>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-500 text-center">
                By submitting, you agree to be contacted at the phone number provided. We will call to confirm your appointment.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
            data-testid="appt-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {step < 4 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="appt-next-btn"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="appt-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
